import React, { useState, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { loanMappingService } from 'api/services/loanMappingService';
import { closingService } from 'api/services/closingService';
import type { PendingClosingCheck } from 'api/types/closing.types';
import { ReturnedHistorySection } from 'components/common/ReturnedHistorySection';
import { ModalLoadingSkeleton } from 'components/common';
import { approvalService } from 'api/services/approvalService';

// ============================================================================
// Types
// ============================================================================

/** GL from a loan mapping that can be selected for closing */
export interface MappingGLOption {
  mappedGlId: string;
  glNumber: string;
  customerName: string;
  netWeight: number;
  allocationRatio: number;
  status: string; // ACTIVE or CLOSED
}

/** Mock mapping summary */
export interface MappingOption {
  id: string;
  mappingRef: string;
  customerName: string;
  customerId: string;
  loanAccountNumber: string;
  approvedAmount: number;
  outstandingPrincipal: number;
  outstandingInterest: number;
  gls: MappingGLOption[];
}

/** GL entry form data (selected GLs with outstanding amounts) */
export interface ClosingGLFormData {
  mappedGlId: string;
  glNumber: string;
  customerName: string;
  netWeight: number;
  allocationRatio: number;
  selected: boolean;
  outstandingPrincipal: string;
  outstandingInterest: string;
  /** CR-001: optional per-GL token loss charge, added to this GL's settlement (cash-flow only) */
  tokenLossCharge: string;
}

/** Form data for creating/editing a closing transaction */
export interface ClosingFormData {
  mappingId: string;
  mappingRef: string;
  customerName: string;
  customerId: string;
  loanAccountNumber: string;
  approvedAmount: number;
  addonOutstandingPrincipal: string;
  addonOutstandingInterest: string;
  glEntries: ClosingGLFormData[];
}

/** Closing record for the view modal */
export interface ClosingRecord {
  id: string;
  closingRef?: string;
  mappingId: string;
  mappingRef?: string;
  customerName: string;
  customerId: string;
  loanAccountNumber: string;
  addonOutstandingPrincipal: number;
  addonOutstandingInterest: number;
  combinedRatio: number;
  addonSettlementPrincipal: number;
  addonSettlementInterest: number;
  glSettlement: number;
  glEntries?: {
    id: string;
    glNumber: string;
    glCustomerName?: string;
    netWeight: number;
    allocationRatio: number;
    outstandingPrincipal: number;
    outstandingInterest: number;
    tokenLossCharge?: number;
    glSettlement?: number | null;
  }[];
  statusCode?: string;
  statusName?: string;
  status?: string;
  submittedBy?: string;
  submittedByName?: string;
  submittedAt?: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: string;
  createdBy?: string;
  createdByName?: string;
  createdAt: string;
  rowVersion?: number;
  businessUnitName?: string;
}

// ============================================================================
// Section Header Style (reused)
// ============================================================================

const sectionHeaderStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  marginBottom: '0.75rem',
  color: 'var(--primary)',
  borderBottom: '2px solid var(--primary)',
  paddingBottom: '0.5rem',
};

// ============================================================================
// Form Modal
// ============================================================================

interface ClosingFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  formData: ClosingFormData;
  mappingOptions: MappingOption[];
  onFormDataChange: (data: ClosingFormData) => void;
  onSave: () => void;
  onSubmit?: () => void;
  onClose: () => void;
}

export const ClosingFormModal: React.FC<ClosingFormModalProps> = ({
  isOpen,
  mode,
  formData,
  mappingOptions: _mappingOptions,
  onFormDataChange,
  onSave,
  onSubmit,
  onClose,
}) => {
  // Search state (for create mode mandatory search)
  const [searchAccountNo, setSearchAccountNo] = useState('');
  const [searchGLAccountNo, setSearchGLAccountNo] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [validated, setValidated] = useState(false);
  const [pendingCheck, setPendingCheck] = useState<PendingClosingCheck | null>(null);

  // Reset search state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchAccountNo('');
      setSearchGLAccountNo('');
      setSearchPerformed(mode === 'edit');
      setSearchError('');
      setValidated(false);
      setPendingCheck(null);
    }
  }, [isOpen, mode]);

  if (!isOpen) return null;

  const isSearchDone = mode === 'edit' || (searchPerformed && !!formData.mappingId);

  // Calculations
  const selectedGLs = formData.glEntries.filter((gl) => gl.selected);
  const combinedRatio = selectedGLs.reduce((s, gl) => s + gl.allocationRatio, 0);
  const addonPrincipal = parseFloat(formData.addonOutstandingPrincipal) || 0;
  const addonInterest = parseFloat(formData.addonOutstandingInterest) || 0;
  const settlementPrincipal = Math.round(addonPrincipal * combinedRatio);
  const settlementInterest = addonInterest;
  const totalGLPrincipal = selectedGLs.reduce((s, gl) => s + (parseFloat(gl.outstandingPrincipal) || 0), 0);
  const totalGLInterest = selectedGLs.reduce((s, gl) => s + (parseFloat(gl.outstandingInterest) || 0), 0);
  // CR-001: token loss is a customer-paid charge added to the settlement (does not affect ratios).
  const totalTokenLoss = selectedGLs.reduce((s, gl) => s + (parseFloat(gl.tokenLossCharge) || 0), 0);
  const totalSettlement = totalGLPrincipal + totalGLInterest + totalTokenLoss + settlementPrincipal + settlementInterest;

  // GL validation: selected GLs with missing outstanding amounts
  const glsWithMissingAmounts = selectedGLs.filter(
    (gl) => !gl.outstandingPrincipal || isNaN(parseFloat(gl.outstandingInterest))
  );

  // ---- Search Handler ----
  const handleSearch = async () => {
    const accNo = searchAccountNo.trim().replace(/\s/g, '');
    const glAccNo = searchGLAccountNo.trim().replace(/\s/g, '');

    if (!accNo && !glAccNo) {
      setSearchError('Enter an Account Number or GL Account Number to search.');
      return;
    }

    setSearchLoading(true);
    setSearchError('');

    try {
      const result = await loanMappingService.searchByAccount(
        accNo || undefined,
        glAccNo || undefined
      );

      if (!result) {
        setSearchError('No matching loan mapping found. Please verify the account number.');
        setSearchPerformed(true);
        return;
      }

      // Check for pending closing requests on this mapping
      setPendingCheck(null);
      try {
        const checkResult = await closingService.checkPending(String(result.id));
        if (checkResult.data?.hasPending) {
          setPendingCheck(checkResult.data);
        }
      } catch {
        // Non-fatal: proceed even if check fails (backend will still validate on save)
      }

      onFormDataChange({
        ...formData,
        mappingId: String(result.id),
        mappingRef: `MAP-${String(result.id).substring(0, 8).toUpperCase()}`,
        customerName: result.customerName ?? '',
        customerId: result.customerId ?? '',
        loanAccountNumber: result.addonLoanAccountNumber ?? '',
        approvedAmount: result.approvedAmount,
        addonOutstandingPrincipal: '',
        addonOutstandingInterest: '',
        glEntries: (result.mappedGls ?? [])
          .filter((gl) => gl.statusCode === 'OPEN')
          .map((gl) => ({
            mappedGlId: String(gl.id),
            glNumber: gl.glNumber ?? '',
            customerName: gl.glCustomerName ?? '',
            netWeight: gl.netWeight,
            allocationRatio: gl.allocationRatio,
            selected: false,
            outstandingPrincipal: '',
            outstandingInterest: '',
            tokenLossCharge: '',
          })),
      });
      setSearchPerformed(true);
      setSearchError('');
    } catch {
      setSearchError('Failed to search. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleResetSearch = () => {
    setSearchAccountNo('');
    setSearchGLAccountNo('');
    setSearchPerformed(false);
    setSearchError('');
    setPendingCheck(null);
    onFormDataChange({
      ...formData,
      mappingId: '',
      mappingRef: '',
      customerName: '',
      customerId: '',
      loanAccountNumber: '',
      approvedAmount: 0,
      addonOutstandingPrincipal: '',
      addonOutstandingInterest: '',
      glEntries: [],
    });
  };

  // ---- GL Handlers ----
  const handleGLToggle = (mappedGlId: string) => {
    onFormDataChange({
      ...formData,
      glEntries: formData.glEntries.map((gl) =>
        gl.mappedGlId === mappedGlId ? { ...gl, selected: !gl.selected } : gl
      ),
    });
  };

  const handleGLFieldChange = (mappedGlId: string, field: 'outstandingPrincipal' | 'outstandingInterest' | 'tokenLossCharge', value: string) => {
    onFormDataChange({
      ...formData,
      glEntries: formData.glEntries.map((gl) =>
        gl.mappedGlId === mappedGlId ? { ...gl, [field]: value } : gl
      ),
    });
  };

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title">
            {mode === 'create' ? 'New Closing Transaction' : 'Edit Closing Transaction'}
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* ========== SECTION A: Search / Loan Details ========== */}
          <h3 style={sectionHeaderStyle}>A. Loan Details</h3>

          {/* Search Interface (create mode, before search) */}
          {mode === 'create' && !isSearchDone && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                Search by Account Number or GL Account Number to find the loan mapping.
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'flex-end' }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Account Number</label>
                  <input
                    className="form-input"
                    type="text"
                    value={searchAccountNo}
                    onChange={(e) => setSearchAccountNo(e.target.value.replace(/\s/g, ''))}
                    placeholder="e.g., ACC-2025-001"
                    style={{ border: '1px solid #334155' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">GL Account Number</label>
                  <input
                    className="form-input"
                    type="text"
                    value={searchGLAccountNo}
                    onChange={(e) => setSearchGLAccountNo(e.target.value.replace(/\s/g, ''))}
                    placeholder="e.g., GL-2000"
                    style={{ border: '1px solid #334155' }}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
                <button
                  className="btn btn-primary"
                  onClick={handleSearch}
                  disabled={searchLoading}
                  style={{ height: '34px', display: 'flex', alignItems: 'center', gap: '0.375rem', whiteSpace: 'nowrap', ...(searchLoading ? { opacity: 0.7, cursor: 'wait' } : {}) }}
                >
                  <IconSystem.search size={16} />
                  {searchLoading ? 'Searching...' : 'Search'}
                </button>
              </div>
              {searchError && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  marginTop: '0.75rem', padding: '0.625rem 0.875rem',
                  background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.375rem',
                  color: '#dc2626', fontSize: '0.8rem',
                }}>
                  <IconSystem.alert size={14} />
                  {searchError}
                </div>
              )}
            </div>
          )}

          {/* Found Loan Details (read-only display) */}
          {isSearchDone && (
            <div style={{ marginBottom: '1.5rem' }}>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem',
                background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Mapping Reference</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{formData.mappingRef}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer</div>
                  <div style={{ fontWeight: 600 }}>{formData.customerName} ({formData.customerId})</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Account Number</div>
                  <div style={{ fontWeight: 600, fontFamily: 'monospace' }}>{formData.loanAccountNumber}</div>
                </div>
              </div>
              {mode === 'create' && (
                <button
                  type="button"
                  onClick={handleResetSearch}
                  style={{
                    marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--primary)', fontSize: '0.75rem', padding: '0.25rem 0',
                    textDecoration: 'underline',
                  }}
                >
                  ← Search Again
                </button>
              )}
            </div>
          )}

          {/* Pending Closing Warning */}
          {isSearchDone && pendingCheck?.hasPending && (
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.625rem',
              padding: '0.875rem 1rem', marginBottom: '1.25rem',
              background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem',
              color: '#991b1b', fontSize: '0.8rem', lineHeight: 1.5,
            }}>
              <IconSystem.alert size={18} style={{ flexShrink: 0, marginTop: '1px', color: '#dc2626' }} />
              <div>
                <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>Pending Closing Request Exists</div>
                <div>{pendingCheck.message}</div>
              </div>
            </div>
          )}

          {/* ========== SECTION B: Add-On Loan Outstanding ========== */}
          {isSearchDone && !pendingCheck?.hasPending && (
            <>
              <h3 style={sectionHeaderStyle}>B. Add-On Loan Outstanding</h3>
              <div style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem',
              }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Outstanding Principal *</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.addonOutstandingPrincipal}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingPrincipal: e.target.value })}
                    placeholder="0"
                    min={0}
                    step="1"
                    style={{ border: `1px solid ${validated && !formData.addonOutstandingPrincipal ? '#dc2626' : '#334155'}` }}
                  />
                  {validated && !formData.addonOutstandingPrincipal && (
                    <span style={{ fontSize: '0.7rem', color: '#dc2626', marginTop: '0.25rem', display: 'block' }}>Required</span>
                  )}
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Outstanding Interest</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.addonOutstandingInterest}
                    onChange={(e) => onFormDataChange({ ...formData, addonOutstandingInterest: e.target.value })}
                    placeholder="0"
                    min={0}
                    step="1"
                    style={{ border: `1px solid #334155` }}
                  />
                </div>
              </div>
            </>
          )}

          {/* ========== SECTION C: GL Selection ========== */}
          {isSearchDone && !pendingCheck?.hasPending && formData.glEntries.length > 0 && (
            <>
              <h3 style={sectionHeaderStyle}>C. Select Gold Loans for Closing</h3>

              {/* GL Validation Alert (shown after Save/Submit attempted) */}
              {validated && glsWithMissingAmounts.length > 0 && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
                  padding: '0.75rem 1rem', marginBottom: '1rem',
                  background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem',
                  color: '#92400e', fontSize: '0.8rem', fontWeight: 500,
                }}>
                  <IconSystem.warning size={16} style={{ flexShrink: 0, marginTop: '1px' }} />
                  <span>
                    Outstanding Principal and Interest are required for selected Gold Loans:{' '}
                    <strong>{glsWithMissingAmounts.map((gl) => gl.glNumber).join(', ')}</strong>
                  </span>
                </div>
              )}

              <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg-tertiary)' }}>
                      <th style={{ padding: '0.5rem', textAlign: 'center', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)', width: '40px' }}>Select</th>
                      <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Number</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Eligible Weight (g)</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Alloc. Ratio</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding Principal</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding Interest</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Token Loss</th>
                      <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Settlement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.glEntries.map((gl) => {
                      const isMissing = validated && gl.selected && (!gl.outstandingPrincipal || isNaN(parseFloat(gl.outstandingInterest)));
                      return (
                        <tr key={gl.mappedGlId} style={{
                          borderBottom: '1px solid var(--border-color)',
                          background: isMissing ? 'rgba(245, 158, 11, 0.06)' : gl.selected ? 'rgba(59, 130, 246, 0.06)' : 'transparent',
                        }}>
                          <td style={{ padding: '0.5rem', textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={gl.selected}
                              onChange={() => handleGLToggle(gl.mappedGlId)}
                              style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={gl.outstandingPrincipal}
                              onChange={(e) => handleGLFieldChange(gl.mappedGlId, 'outstandingPrincipal', e.target.value)}
                              disabled={!gl.selected}
                              style={{
                                width: '120px', textAlign: 'right', fontSize: '0.8rem', padding: '0.25rem 0.5rem',
                                borderColor: isMissing && !gl.outstandingPrincipal ? '#f59e0b' : undefined,
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={gl.outstandingInterest}
                              onChange={(e) => handleGLFieldChange(gl.mappedGlId, 'outstandingInterest', e.target.value)}
                              disabled={!gl.selected}
                              style={{
                                width: '120px', textAlign: 'right', fontSize: '0.8rem', padding: '0.25rem 0.5rem',
                                borderColor: isMissing && isNaN(parseFloat(gl.outstandingInterest)) ? '#f59e0b' : undefined,
                              }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                            <input
                              className="form-input"
                              type="number"
                              min="0"
                              step="1"
                              placeholder="0"
                              value={gl.tokenLossCharge}
                              onChange={(e) => handleGLFieldChange(gl.mappedGlId, 'tokenLossCharge', e.target.value)}
                              disabled={!gl.selected}
                              title="Optional — added to this GL's settlement; does not affect ratios"
                              style={{ width: '110px', textAlign: 'right', fontSize: '0.8rem', padding: '0.25rem 0.5rem' }}
                            />
                          </td>
                          <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)' }}>
                            {gl.selected
                              ? formatCurrency((parseFloat(gl.outstandingPrincipal) || 0) + (parseFloat(gl.outstandingInterest) || 0) + (parseFloat(gl.tokenLossCharge) || 0))
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* ========== SECTION D: Settlement Calculation ========== */}
          {isSearchDone && !pendingCheck?.hasPending && selectedGLs.length > 0 && (
            <>
              <h3 style={sectionHeaderStyle}>D. Settlement Calculation</h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem',
                marginBottom: '1rem',
                background: 'var(--bg-secondary)',
                padding: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Combined Ratio</div>
                  <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{(combinedRatio * 100).toFixed(2)}%</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Principal</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(settlementPrincipal)}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>{formatCurrency(addonPrincipal)} x {(combinedRatio * 100).toFixed(2)}%</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Interest</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(settlementInterest)}</div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>Full Amount</div>
                </div>
              </div>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: '1rem',
                background: 'var(--bg-tertiary)',
                padding: '1rem',
                borderRadius: '0.5rem',
                border: '1px solid var(--border-color)',
              }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>GL Outstanding (P+I)</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(totalGLPrincipal + totalGLInterest)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Add-On Settlement (P+I)</div>
                  <div style={{ fontWeight: 600 }}>{formatCurrency(settlementPrincipal + settlementInterest)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Settlement</div>
                  <div style={{ fontWeight: 700, fontSize: '1rem', color: '#16a34a' }}>{formatCurrency(totalSettlement)}</div>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
          <div>
            {validated && glsWithMissingAmounts.length > 0 && selectedGLs.length > 0 && (
              <span style={{ fontSize: '0.75rem', color: '#d97706', fontWeight: 600 }}>
                Missing GL outstanding amounts
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={() => { setValidated(true); onSave(); }}
              disabled={!!pendingCheck?.hasPending}
              style={pendingCheck?.hasPending ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
            >
              <IconSystem.save size={16} />
              {mode === 'create' ? 'Create as Draft' : 'Save as Draft'}
            </button>
            {onSubmit && (
              <button
                className="btn btn-primary"
                onClick={() => { setValidated(true); onSubmit(); }}
                disabled={!!pendingCheck?.hasPending}
                style={{
                  background: '#16a34a', borderColor: '#16a34a',
                  ...(pendingCheck?.hasPending ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                }}
              >
                <IconSystem.check size={16} />
                Submit
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// View Modal
// ============================================================================

interface ClosingViewModalProps {
  isOpen: boolean;
  record: ClosingRecord | null;
  onClose: () => void;
}

export const ClosingViewModal: React.FC<ClosingViewModalProps> = ({
  isOpen,
  record,
  onClose,
}) => {
  const [glEntries, setGlEntries] = useState<ClosingRecord['glEntries']>([]);
  const [glLoading, setGlLoading] = useState(false);
  const [detailCreatedByName, setDetailCreatedByName] = useState<string | undefined>();
  const [approvalInfo, setApprovalInfo] = useState<{
    approvedBy?: string; approvedAt?: string;
    rejectedBy?: string; rejectedAt?: string; rejectedRemark?: string | null;
  }>({});

  useEffect(() => {
    if (isOpen && record?.id) {
      setGlLoading(true);
      setGlEntries([]);
      setDetailCreatedByName(undefined);
      setApprovalInfo({});
      closingService.getById(record.id)
        .then((response) => {
          const detail = response.data;
          if (detail) {
            setDetailCreatedByName(detail.createdByName);
            if (detail.glEntries) {
              setGlEntries(detail.glEntries.map((gl) => ({
                id: String(gl.id),
                glNumber: gl.glNumber ?? '',
                glCustomerName: gl.glCustomerName,
                netWeight: gl.netWeight,
                allocationRatio: gl.allocationRatio,
                outstandingPrincipal: gl.outstandingPrincipal,
                outstandingInterest: gl.outstandingInterest,
              })));
            }
          }
        })
        .catch(() => { /* handled silently */ })
        .finally(() => setGlLoading(false));
      approvalService.getHistory('closing', record.id)
        .then((res) => {
          const history = res.data ?? [];
          const approved = history.find(h => h.action?.toLowerCase() === 'approved');
          const rejected = history.find(h => h.action?.toLowerCase() === 'rejected');
          setApprovalInfo({
            approvedBy: approved?.actionedByName, approvedAt: approved?.actionedAt,
            rejectedBy: rejected?.actionedByName, rejectedAt: rejected?.actionedAt, rejectedRemark: rejected?.remark ?? undefined,
          });
        })
        .catch(() => {});
    } else {
      setGlEntries([]);
      setDetailCreatedByName(undefined);
      setApprovalInfo({});
    }
  }, [isOpen, record?.id]);

  if (!isOpen || !record) return null;

  const entries = glEntries ?? [];
  const totalGLPrincipal = entries.reduce((s, gl) => s + gl.outstandingPrincipal, 0);
  const totalGLInterest = entries.reduce((s, gl) => s + gl.outstandingInterest, 0);
  const totalTokenLoss = entries.reduce((s, gl) => s + (gl.tokenLossCharge ?? 0), 0);
  const totalSettlement = totalGLPrincipal + totalGLInterest + totalTokenLoss + record.addonSettlementPrincipal + record.addonSettlementInterest;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-lg" style={{ maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <h2 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            Closing {record.closingRef}
            <span style={getStatusBadgeStyle(record.statusCode ?? '')}>{(record.statusCode ?? '').replace(/_/g, ' ')}</span>
          </h2>
          <button className="modal-close" onClick={onClose}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content" style={{ overflow: 'auto', flex: 1 }}>
          {/* Section 1: Request Details Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Request Details
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Mapping Reference</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.mappingRef}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Account Number</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.loanAccountNumber}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer ID</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem', fontFamily: 'monospace' }}>{record.customerId}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Customer Name</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.customerName}</div>
              </div>
            </div>
          </div>

          {/* Section 2: Settlement Summary Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Settlement Summary
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Principal</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingPrincipal)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Outstanding Interest</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonOutstandingInterest)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Combined Ratio</div>
                <div style={{ fontWeight: 600, color: 'var(--primary)' }}>{(record.combinedRatio * 100).toFixed(2)}%</div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Principal</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementPrincipal)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Settlement Interest</div>
                <div style={{ fontWeight: 600 }}>{formatCurrency(record.addonSettlementInterest)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Total Settlement</div>
                <div style={{ fontWeight: 700, color: '#16a34a', fontSize: '1rem' }}>{formatCurrency(totalSettlement)}</div>
              </div>
            </div>
          </div>

          {/* Section 3: GL Entries Table */}
          <h3 style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.75rem', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
            Selected Gold Loans
          </h3>
          {glLoading ? (
            <ModalLoadingSkeleton sections={2} showTable tableCols={5} tableRows={4} />
          ) : (
          <div style={{ overflowX: 'auto', marginBottom: '1.5rem' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: 'var(--bg-tertiary)' }}>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>#</th>
                  <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Number</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Eligible Weight (g)</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Alloc. Ratio</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding P</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Outstanding I</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>Token Loss</th>
                  <th style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600, fontSize: '0.75rem', borderBottom: '2px solid var(--border-color)' }}>GL Settlement</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((gl, idx) => (
                  <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '0.5rem', color: 'var(--text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{gl.glNumber}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{gl.netWeight.toFixed(3)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 500 }}>{(gl.allocationRatio * 100).toFixed(2)}%</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.outstandingPrincipal)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.outstandingInterest)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(gl.tokenLossCharge ?? 0)}</td>
                    <td style={{ padding: '0.5rem', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(gl.glSettlement ?? (gl.outstandingPrincipal + gl.outstandingInterest + (gl.tokenLossCharge ?? 0)))}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--bg-tertiary)', fontWeight: 600 }}>
                  <td colSpan={2} style={{ padding: '0.5rem' }}>Total</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{entries.reduce((s, gl) => s + gl.netWeight, 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{(record.combinedRatio * 100).toFixed(2)}%</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(totalGLPrincipal)}</td>
                  <td style={{ padding: '0.5rem', textAlign: 'right' }}>{formatCurrency(totalGLInterest)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}

          {/* Section 4: Submission & Approval Info Card */}
          <div style={{ marginBottom: '1.5rem', background: 'var(--bg-secondary)', padding: '1rem', borderRadius: '0.5rem', border: '1px solid var(--border-color)' }}>
            <h3 style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.75rem 0', borderBottom: '2px solid var(--primary)', paddingBottom: '0.5rem' }}>
              Submission & Approval Info
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{detailCreatedByName || record.createdByName || record.createdBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Created At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{formatDate(record.createdAt)}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted By</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedByName || record.submittedBy || '-'}</div>
              </div>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Submitted At</div>
                <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{record.submittedAt ? formatDate(record.submittedAt) : '-'}</div>
              </div>
            </div>
            {approvalInfo.approvedBy && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved By</div>
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#16a34a' }}>{approvalInfo.approvedBy}</div>
                </div>
                <div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Approved At</div>
                  <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.approvedAt ? formatDate(approvalInfo.approvedAt) : '-'}</div>
                </div>
              </div>
            )}
            {approvalInfo.rejectedBy && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginTop: '1rem', borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected By</div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#dc2626' }}>{approvalInfo.rejectedBy}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejected At</div>
                    <div style={{ fontWeight: 500, fontSize: '0.85rem' }}>{approvalInfo.rejectedAt ? formatDate(approvalInfo.rejectedAt) : '-'}</div>
                  </div>
                </div>
                {approvalInfo.rejectedRemark && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: '0.25rem' }}>Rejection Remark</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>{approvalInfo.rejectedRemark}</div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Section 5: Workflow Information — Returned History
              Shown for RETURNED status (active return cycle) AND for REJECTED records
              that came from a prior return, so the return trail stays visible after rejection. */}
          {(record.statusCode === 'RETURNED' || approvalInfo.rejectedBy) && (
            <ReturnedHistorySection recordType="closing" recordId={record.id} />
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};
